import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import SearchableSelect from './SearchableSelect';
import { adminAPI, erpAPI } from '../../services/api';
import './AdminFormStyles.css';

interface CouponFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  coupon?: any | null;
}

const CouponFormModal: React.FC<CouponFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  coupon
}) => {
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_purchase_amount: '',
    max_discount_amount: '',
    usage_limit: '',
    valid_from: '',
    valid_to: '',
    scope: 'WEBSITE',
    shop: '',
    target_type: 'ALL_PRODUCTS',
    category: '',
    product_ids: [] as number[],
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [customerOptions,setCustomerOptions]=useState<any[]>([]);
  const [eligibleCustomers,setEligibleCustomers]=useState<any[]>([]);
  const [customerSearch,setCustomerSearch]=useState('');
  const [shops,setShops]=useState<any[]>([]);
  const [categories,setCategories]=useState<any[]>([]);
  const [productOptions,setProductOptions]=useState<any[]>([]);
  const [selectedProducts,setSelectedProducts]=useState<any[]>([]);

  useEffect(() => {
    if (coupon) {
      setFormData({
        code: coupon.code || '',
        discount_type: coupon.discount_type || 'percentage',
        discount_value: coupon.discount_value || '',
        min_purchase_amount: coupon.min_purchase_amount || '',
        max_discount_amount: coupon.max_discount_amount || '',
        usage_limit: coupon.usage_limit || '',
        valid_from: coupon.valid_from ? coupon.valid_from.split('T')[0] : '',
        valid_to: coupon.valid_to ? coupon.valid_to.split('T')[0] : '',
        scope: coupon.scope || 'WEBSITE',
        shop: coupon.shop || '',
        target_type: coupon.target_type || 'ALL_PRODUCTS',
        category: coupon.category || '',
        product_ids: (coupon.target_products||[]).map((x:any)=>x.id),
        is_active: coupon.is_active ?? true
      });
      setEligibleCustomers(coupon.eligible_customers||[]);
      setSelectedProducts(coupon.target_products||[]);
    } else {
      setFormData({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        min_purchase_amount: '',
        max_discount_amount: '',
        usage_limit: '',
        valid_from: '',
        valid_to: '',
        scope: 'WEBSITE',
        shop: '',
        target_type: 'ALL_PRODUCTS',
        category: '',
        product_ids: [],
        is_active: true
      });
      setEligibleCustomers([]);
      setSelectedProducts([]);
    }
    setErrors({});
  }, [coupon, isOpen]);

  useEffect(()=>{if(!isOpen)return;const timer=setTimeout(async()=>{try{const response=await adminAPI.getCustomers({search:customerSearch,page_size:30});const rows=Array.isArray(response.data)?response.data:(response.data.results||[]);setCustomerOptions(rows)}catch{}},300);return()=>clearTimeout(timer)},[customerSearch,isOpen]);
  useEffect(()=>{if(isOpen)erpAPI.list('locations').then(response=>{const list=Array.isArray(response.data)?response.data:(response.data.results||[]);setShops(list.filter((x:any)=>x.location_type==='SHOP'))}).catch(()=>setShops([]))},[isOpen]);
  useEffect(()=>{if(!isOpen)return;Promise.all([adminAPI.getCategories({page_size:100}),adminAPI.getProducts({page_size:50})]).then(([c,p])=>{setCategories(Array.isArray(c.data)?c.data:(c.data.results||[]));setProductOptions(Array.isArray(p.data)?p.data:(p.data.results||[]))}).catch(()=>{})},[isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors: any = {};

    if (!formData.code.trim()) newErrors.code = 'Coupon code is required';
    if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
      newErrors.discount_value = 'Discount value must be greater than 0';
    }
    if (formData.discount_type === 'percentage' && parseFloat(formData.discount_value) > 100) {
      newErrors.discount_value = 'Percentage cannot exceed 100%';
    }
    if (formData.valid_from && formData.valid_to && formData.valid_to <= formData.valid_from) {
      newErrors.valid_to = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        code: formData.code.toUpperCase(),
        min_purchase_amount: formData.min_purchase_amount || 0,
        max_discount_amount: formData.max_discount_amount || null,
        usage_limit: formData.usage_limit || null
        ,eligible_customer_ids: eligibleCustomers.map(x=>x.id)
      };
      await onSave(submitData);
      onClose();
    } catch (error: any) {
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={coupon ? 'Edit Coupon' : 'Add Coupon'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="admin-form-modal">
        <div className="admin-form-table">
          {/* Coupon Code */}
          <div className="admin-form-row">
            <div className="admin-form-label">Coupon Code *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="e.g., SUMMER20"
                className={errors.code ? 'has-error' : ''}
                style={{ textTransform: 'uppercase' }}
              />
              {errors.code && <span className="error-text">{errors.code}</span>}
            </div>
          </div>

          {/* Discount Type */}
          <div className="admin-form-row"><div className="admin-form-label">Sales channel *</div><div className="admin-form-input"><SearchableSelect value={formData.scope} onChange={scope=>setFormData(prev=>({...prev,scope,shop:scope==='SHOP'?prev.shop:''}))} placeholder="Choose scope" options={[{value:'ALL_CHANNELS',label:'All shops + website'},{value:'ALL_SHOPS',label:'All shops'},{value:'SHOP',label:'Particular shop'},{value:'WEBSITE',label:'Website orders only'}]}/></div></div>
          {formData.scope==='SHOP'&&<div className="admin-form-row"><div className="admin-form-label">Shop *</div><div className="admin-form-input"><SearchableSelect value={formData.shop} onChange={shop=>setFormData(prev=>({...prev,shop}))} placeholder="Select shop" options={shops.map(x=>({value:x.id,label:x.name}))}/></div></div>}
          <div className="admin-form-row"><div className="admin-form-label">Product target *</div><div className="admin-form-input"><SearchableSelect value={formData.target_type} onChange={target_type=>{setFormData(prev=>({...prev,target_type,category:'',product_ids:[]}));setSelectedProducts([])}} placeholder="Choose products" options={[{value:'ALL_PRODUCTS',label:'All products'},{value:'CATEGORY',label:'One category'},{value:'PRODUCTS',label:'One or more selected products'}]}/></div></div>
          {formData.target_type==='CATEGORY'&&<div className="admin-form-row"><div className="admin-form-label">Category *</div><div className="admin-form-input"><SearchableSelect value={formData.category} onChange={category=>setFormData(prev=>({...prev,category}))} placeholder="Select category" options={categories.map(x=>({value:x.id,label:x.name_en}))}/></div></div>}
          {formData.target_type==='PRODUCTS'&&<div className="admin-form-row"><div className="admin-form-label">Selected products *</div><div className="admin-form-input"><SearchableSelect value="" onChange={id=>{const product=productOptions.find(x=>String(x.id)===String(id));if(product&&!selectedProducts.some(x=>x.id===product.id)){const next=[...selectedProducts,{id:product.id,name:product.name_en}];setSelectedProducts(next);setFormData(prev=>({...prev,product_ids:next.map(x=>x.id)}))}}} onSearch={async search=>{const response=await adminAPI.getProducts({search,page_size:50});setProductOptions(Array.isArray(response.data)?response.data:(response.data.results||[]))}} placeholder="Search and add products" options={productOptions.map(x=>({value:x.id,label:x.name_en}))}/><div className="erp-chips">{selectedProducts.map(product=><span key={product.id}>{product.name}<button type="button" onClick={()=>{const next=selectedProducts.filter(x=>x.id!==product.id);setSelectedProducts(next);setFormData(prev=>({...prev,product_ids:next.map(x=>x.id)}))}}>×</button></span>)}</div></div></div>}

          <div className="admin-form-row">
            <div className="admin-form-label">Discount Type *</div>
            <div className="admin-form-input">
              <SearchableSelect value={formData.discount_type} onChange={discount_type=>setFormData(prev=>({...prev,discount_type}))} placeholder="Discount type" options={[{value:'percentage',label:'Percentage'},{value:'fixed',label:'Fixed Amount'}]}/>
            </div>
          </div>

          <div className="admin-form-row">
            <div className="admin-form-label">Eligible Customers</div>
            <div className="admin-form-input"><SearchableSelect value="" onChange={id=>{const customer=customerOptions.find(x=>String(x.user_id)===id);if(customer&&!eligibleCustomers.some(x=>x.id===customer.user_id))setEligibleCustomers(rows=>[...rows,{id:customer.user_id,name:customer.full_name,email:customer.email}])}} onSearch={setCustomerSearch} placeholder="Type customer name, username, or email…" options={customerOptions.map(x=>({value:x.user_id,label:`${x.full_name} — ${x.email}`}))}/><div className="erp-chips">{eligibleCustomers.map(x=><span key={x.id}>{x.name||x.email}<button type="button" onClick={()=>setEligibleCustomers(rows=>rows.filter(y=>y.id!==x.id))}>×</button></span>)}</div><span className="admin-form-hint">Leave empty to allow all customers.</span></div>
          </div>

          {/* Discount Value */}
          <div className="admin-form-row">
            <div className="admin-form-label">
              Discount Value * {formData.discount_type === 'percentage' ? '(%)' : '(৳)'}
            </div>
            <div className="admin-form-input">
              <input
                type="number"
                name="discount_value"
                value={formData.discount_value}
                onChange={handleChange}
                step="1"
                min="0"
                className={errors.discount_value ? 'has-error' : ''}
              />
              {errors.discount_value && <span className="error-text">{errors.discount_value}</span>}
            </div>
          </div>

          {/* Min Purchase Amount */}
          <div className="admin-form-row">
            <div className="admin-form-label">Min Purchase (৳)</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="min_purchase_amount"
                value={formData.min_purchase_amount}
                onChange={handleChange}
                step="1"
                min="0"
              />
            </div>
          </div>

          {/* Max Discount Amount */}
          <div className="admin-form-row">
            <div className="admin-form-label">Max Discount (৳)</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="max_discount_amount"
                value={formData.max_discount_amount}
                onChange={handleChange}
                step="1"
                min="0"
              />
            </div>
          </div>

          {/* Usage Limit */}
          <div className="admin-form-row">
            <div className="admin-form-label">Usage Limit</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="usage_limit"
                value={formData.usage_limit}
                onChange={handleChange}
                min="0"
                placeholder="Unlimited"
              />
            </div>
          </div>

          {/* Valid From */}
          <div className="admin-form-row">
            <div className="admin-form-label">Valid From</div>
            <div className="admin-form-input">
              <input
                type="date"
                name="valid_from"
                value={formData.valid_from}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Valid To */}
          <div className="admin-form-row">
            <div className="admin-form-label">Valid To</div>
            <div className="admin-form-input">
              <input
                type="date"
                name="valid_to"
                value={formData.valid_to}
                onChange={handleChange}
                className={errors.valid_to ? 'has-error' : ''}
              />
              {errors.valid_to && <span className="error-text">{errors.valid_to}</span>}
            </div>
          </div>

          {/* Active Status */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Status</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <span>Active Coupon</span>
              </label>
            </div>
          </div>
        </div>

        <div className="admin-form-actions">
          <button type="button" onClick={onClose} className="btn-cancel" disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CouponFormModal;
